import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtPct, fmtUSDExact } from "@/lib/format";
import TradeDialog from "@/components/TradeDialog";

export default function Portfolio() {
  const [portfolio, setPortfolio] = useState({ cash: 0, holdings: [], total_value: 0, total_market_value: 0 });
  const [txs, setTxs] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => {
    api.get("/portfolio").then(({ data }) => setPortfolio(data)).catch(() => {});
    api.get("/transactions").then(({ data }) => setTxs(data)).catch(() => {});
  };

  useEffect(load, []);

  const openSell = (h) => {
    setSelected({ type: h.asset_type, symbol: h.symbol, name: h.name, price: h.price });
    setOpen(true);
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="portfolio-page">
      <header className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">// portfolio</div>
        <h1 className="mt-2 text-4xl md:text-5xl font-black tracking-tighter uppercase">Portfolio</h1>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Stat label="Total value" value={fmtUSDExact(portfolio.total_value)} testid="portfolio-total" />
        <Stat label="Holdings value" value={fmtUSDExact(portfolio.total_market_value)} testid="portfolio-holdings-value" />
        <Stat label="Cash" value={fmtUSDExact(portfolio.cash)} testid="portfolio-cash" />
      </section>

      <section className="border border-neo-border bg-neo-surface mb-8" data-testid="portfolio-holdings-table">
        <div className="p-4 border-b border-neo-border font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          // holdings · {portfolio.holdings.length}
        </div>
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-neo-border font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          <div className="col-span-2">Sym</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-1 text-right">Qty</div>
          <div className="col-span-2 text-right">Price</div>
          <div className="col-span-2 text-right">Market</div>
          <div className="col-span-1 text-right">P&L</div>
          <div className="col-span-1 text-right">Act.</div>
        </div>
        <div className="divide-y divide-neo-border font-mono text-sm">
          {portfolio.holdings.length === 0 && (
            <div className="p-8 text-center text-neutral-500 font-mono text-xs uppercase tracking-widest">
              No holdings yet. Buy some assets from markets →
            </div>
          )}
          {portfolio.holdings.map((h) => (
            <div key={h.id} className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-white/5 transition-colors">
              <div className="col-span-2 text-white uppercase">{h.symbol}</div>
              <div className="col-span-3 truncate text-neutral-300">{h.name}</div>
              <div className="col-span-1 text-right">{Number(h.quantity).toFixed(4)}</div>
              <div className="col-span-2 text-right">{fmtUSDExact(h.price, h.price < 1 ? 4 : 2)}</div>
              <div className="col-span-2 text-right text-white">{fmtUSDExact(h.market_value)}</div>
              <div className={`col-span-1 text-right ${h.pnl >= 0 ? "pos" : "neg"}`}>
                {fmtPct(h.pnl_pct)}
              </div>
              <div className="col-span-1 text-right">
                <button
                  data-testid={`portfolio-sell-${h.symbol}`}
                  onClick={() => openSell(h)}
                  className="border border-neo-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-white/5 transition-colors"
                >
                  Sell
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border border-neo-border bg-neo-surface" data-testid="portfolio-transactions-table">
        <div className="p-4 border-b border-neo-border font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          // transaction_history
        </div>
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-neo-border font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          <div className="col-span-3">Date</div>
          <div className="col-span-1">Side</div>
          <div className="col-span-2">Sym</div>
          <div className="col-span-2 text-right">Qty</div>
          <div className="col-span-2 text-right">Price</div>
          <div className="col-span-1 text-right">Fee</div>
          <div className="col-span-1 text-right">Total</div>
        </div>
        <div className="divide-y divide-neo-border font-mono text-sm max-h-[50vh] overflow-y-auto">
          {txs.length === 0 && (
            <div className="p-8 text-center text-neutral-500 font-mono text-xs">No transactions</div>
          )}
          {txs.map((t) => (
            <div key={t.id} className="grid grid-cols-12 gap-2 px-4 py-3 items-center">
              <div className="col-span-3 text-neutral-500 text-xs">{new Date(t.created_at).toLocaleString()}</div>
              <div className={`col-span-1 uppercase ${t.side === "buy" ? "pos" : "neg"}`}>{t.side}</div>
              <div className="col-span-2 text-white uppercase">{t.symbol}</div>
              <div className="col-span-2 text-right">{Number(t.quantity).toFixed(4)}</div>
              <div className="col-span-2 text-right">{fmtUSDExact(t.price, t.price < 1 ? 4 : 2)}</div>
              <div className="col-span-1 text-right text-neutral-400">{fmtUSDExact(t.fee)}</div>
              <div className="col-span-1 text-right text-white">{fmtUSDExact(t.total)}</div>
            </div>
          ))}
        </div>
      </section>

      <TradeDialog open={open} onOpenChange={setOpen} asset={selected} onTraded={load} />
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-neo-border bg-neo-surface p-6" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">{label}</div>
      <div className="mt-3 font-mono text-3xl font-bold tracking-tighter">{value}</div>
    </div>
  );
}
